// CustomTimer.tsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { getTicketCount } from "../redux/actions/ticket";

interface CustomTimerProps {
  clearDateTime?: () => void;
  duration?: number; // default: 30 seconds
  onTimerComplete?: () => void;
  timerStatus: boolean; // from Redux
  getTicketCount: () => void; // from mapDispatchToProps
}

const CustomTimer: React.FC<CustomTimerProps> = ({
  clearDateTime,
  duration = 30,
  onTimerComplete,
  timerStatus,
  getTicketCount,
}) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset timer
  const resetTimer = useCallback(() => {
    setTimeLeft(duration);
  }, [duration]);

  // When timer reaches 0
  const handleTimerComplete = useCallback(() => {

    if (clearDateTime) {
      clearDateTime();
    }

    // ✅ Only call API if timerStatus is true
    if (timerStatus) {
      getTicketCount();
    }

    if (onTimerComplete) {
      onTimerComplete();
    }

    resetTimer();
  }, [clearDateTime, getTicketCount, onTimerComplete, resetTimer, timerStatus]);

  // Countdown effect
  useEffect(() => {
    // If timer is OFF, stop countdown & set display to 00:00
    if (!timerStatus) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setTimeLeft(0); // force display to 00:00
      return;
    }

    // If timer is ON, start countdown
    const tick = () => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleTimerComplete();
          return duration;
        }
        return prev - 1;
      });
    };

    if (intervalRef.current === null) {
      intervalRef.current = setInterval(tick, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [duration, handleTimerComplete, timerStatus]);

  // Format mm:ss
  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div>
      <strong
        style={{
          color: timerStatus ? "#20A5D6" : "#ea4b5b",
          fontSize: "2em",
        }}
      >
        {formatTime(timeLeft)}
      </strong>
    </div>
  );
};

// Redux mappings
const mapStateToProps = (state: any) => ({
  timerStatus: state.ticket.timerStatus,
});

const mapDispatchToProps = {
  getTicketCount,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomTimer);
